import {
  getSalesforceOrderSyncStatus,
  startSalesforceOrderSync,
  type SalesforceOrderSyncJob,
} from '@/services/api/salesforce'

export type SalesforceSyncState = SalesforceOrderSyncJob

const idleState = (): SalesforceSyncState => ({
  status: 'idle',
  fetched: 0,
  processed: 0,
  upserted: 0,
  since: '2026-04-01',
  percent: 0,
})

let state: SalesforceSyncState = idleState()
const listeners = new Set<(next: SalesforceSyncState) => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null
let lastNotifiedStatus: SalesforceSyncState['status'] = 'idle'
let sawRunningThisSession = false
const completeListeners = new Set<(next: SalesforceSyncState) => void>()

function emit() {
  listeners.forEach((listener) => listener(state))
}

function apply(job: SalesforceOrderSyncJob): SalesforceSyncState {
  return {
    ...idleState(),
    ...job,
    status: job.status ?? 'idle',
    percent: job.percent ?? 0,
  }
}

async function pollOnce() {
  try {
    const job = await getSalesforceOrderSyncStatus()
    state = apply(job)
    emit()
    if (state.status === 'running') sawRunningThisSession = true
    if (
      sawRunningThisSession &&
      (state.status === 'completed' || state.status === 'failed') &&
      lastNotifiedStatus !== state.status
    ) {
      completeListeners.forEach((listener) => listener(state))
    }
    lastNotifiedStatus = state.status
    if (state.status !== 'running' && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  } catch {
    // Keep the last known state; the next tick retries.
  }
}

function startPolling() {
  if (pollTimer) return
  void pollOnce()
  pollTimer = setInterval(() => {
    void pollOnce()
  }, 1500)
}

export function subscribeSalesforceSync(listener: (next: SalesforceSyncState) => void) {
  listeners.add(listener)
  listener(state)
  return () => {
    listeners.delete(listener)
  }
}

export function onSalesforceSyncSettled(listener: (next: SalesforceSyncState) => void) {
  completeListeners.add(listener)
  return () => {
    completeListeners.delete(listener)
  }
}

export function watchSalesforceSyncInBackground() {
  void pollOnce().then(() => {
    if (state.status === 'running') startPolling()
  })
}

export async function startSalesforceSync(): Promise<SalesforceSyncState> {
  const job = await startSalesforceOrderSync()
  lastNotifiedStatus = 'running'
  sawRunningThisSession = true
  state = apply({ ...job, status: job.status ?? 'running' })
  emit()
  startPolling()
  return state
}

export function salesforceSyncCompleteMessage(job: SalesforceSyncState) {
  if (job.status === 'failed') {
    return job.errorMessage || 'Salesforce Order sync failed.'
  }
  return `${job.upserted} Salesforce Orders refreshed from ${job.since}. Warehouse stock is unchanged.`
}
